import React, { useState, useEffect } from 'react'
import { getCmsCollection } from '../lib/cms'
import { getOptimizedImageUrl, getImageSrcSet } from '../lib/image'
import Lightbox from './Lightbox'

const DEFAULT_GALLERY_IMAGES = [
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1267.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/os-7910068-pexels-photo-7910068-auto-compress-cs-tinysrgb-dpr-2-h-650-w-940.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1900.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/27773421-pexels-photo-27773421-auto-compress-cs-tinysrgb-dpr-2-h-650-w-940.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1173.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1140.jpeg',
]

export default function GallerySection({
  slug = 'hovedgalleri',
  title,
  className = '',
}) {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  useEffect(() => {
    let isMounted = true

    async function loadGallery() {
      try {
        setLoading(true)
        const items = await getCmsCollection('galleri')
        if (!isMounted) return

        // Find the gallery matching the requested slug
        const matched = items.find((item) => {
          const itemSlug = typeof item.slug === 'object' ? item.slug?.da : item.slug
          return itemSlug === slug || item.id === slug
        })

        let resolvedImages = []
        if (matched?.images?.length > 0) {
          resolvedImages = [...matched.images]
          // If the specific service gallery has fewer than 3 images, supplement with main gallery photos to complete a 3-per-row grid
          if (resolvedImages.length < 3) {
            const mainGallery = items.find((item) => {
              const itemSlug = typeof item.slug === 'object' ? item.slug?.da : item.slug
              return itemSlug === 'hovedgalleri'
            })
            const fallbacks = (mainGallery?.images || DEFAULT_GALLERY_IMAGES).filter(
              (url) => !resolvedImages.includes(url)
            )
            resolvedImages = [...resolvedImages, ...fallbacks.slice(0, 3 - resolvedImages.length)]
          }
        } else if (items.length > 0 && items[0]?.images?.length > 0) {
          resolvedImages = items[0].images.slice(0, 6)
        } else {
          resolvedImages = DEFAULT_GALLERY_IMAGES.slice(0, 6)
        }

        setImages(resolvedImages)
      } catch (err) {
        console.error('Failed to load gallery:', err)
        setImages(DEFAULT_GALLERY_IMAGES.slice(0, 6))
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadGallery()

    return () => {
      isMounted = false
    }
  }, [slug])

  const count = images.length

  const openLightbox = (imgIdx) => {
    setLightboxIndex(imgIdx)
    setLightboxOpen(true)
  }

  const displayTitle = title || 'Projektgalleri'

  if (loading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-2 ${className}`}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="aspect-4/3 rounded-xl bg-slate-100 animate-pulse" />
        ))}
      </div>
    )
  }

  if (count === 0) {
    return null
  }

  return (
    <div className={`pt-2 ${className}`}>
      {/* 3 images per row grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((imgUrl, idx) => (
          <div
            key={`${imgUrl}-${idx}`}
            onClick={() => openLightbox(idx)}
            className="group relative aspect-4/3 overflow-hidden rounded-xl bg-slate-100 cursor-pointer border border-slate-200 shadow-xs hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-red-600"
            role="button"
            tabIndex={0}
            aria-label={`Vis forstørret billede ${idx + 1} af ${count}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openLightbox(idx)
              }
            }}
          >
            <img
              src={getOptimizedImageUrl(imgUrl, { width: 600, quality: 80, format: 'auto' })}
              srcSet={getImageSrcSet(imgUrl, [300, 600, 900])}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
            />
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={images}
        currentIndex={lightboxIndex}
        title={displayTitle}
        onClose={() => setLightboxOpen(false)}
        onNext={() => setLightboxIndex((prev) => (prev + 1) % count)}
        onPrev={() => setLightboxIndex((prev) => (prev - 1 + count) % count)}
      />
    </div>
  )
}
